/**
 * 상단 헤더: 로고·LIVE/MOCK 배지·전체 상태·요약 지표·일시정지·화면 맞춤·패널 토글.
 */
import { AlertTriangle, CheckCircle2, Clock, Gauge, Maximize2, PanelLeft, PanelRight, Pause, Percent, Play, Waves, XCircle, Workflow, } from "lucide-react";
import { memo } from "react";
import { formatLatency, formatNumber, formatPercent, formatRelative, formatTimeTooltip } from "../../lib/format";
import { selectActions, selectError, selectFeedStatus, selectGeneratedAt, selectMode, selectPaused, selectSummary, selectSystemStatus, usePipelineStore, } from "../../store/pipeline-store";
import { useNow } from "./hooks";
import { IconButton, cn } from "./ui";
const SYSTEM_LABEL = {
    healthy: "전체 정상",
    warning: "주의 필요",
    error: "장애 발생",
};
const SYSTEM_ICON = { healthy: CheckCircle2, warning: AlertTriangle, error: XCircle };
const SYSTEM_COLOR = {
    healthy: "var(--status-healthy)",
    warning: "var(--status-warning)",
    error: "var(--status-error)",
};
function ModeBadge() {
    const mode = usePipelineStore(selectMode);
    const feedStatus = usePipelineStore(selectFeedStatus);
    const error = usePipelineStore(selectError);
    if (mode === "idle") {
        return (<span className="inline-flex h-6 items-center gap-1.5 rounded-full border border-border px-2 text-[11px] font-semibold text-fg-muted" title={feedStatus === "error" ? (error ?? "API 오류") : "첫 스냅샷 대기 중"} aria-live="polite">
        <span aria-hidden className="size-2 rounded-full bg-fg-subtle"/>
        {feedStatus === "error" ? "오류" : "연결 중"}
      </span>);
    }
    if (mode === "db") {
        return (<span className="inline-flex h-6 items-center gap-1.5 rounded-full border border-status-healthy/40 bg-status-healthy/10 px-2 text-[11px] font-semibold text-status-healthy" title="실시간: MariaDB(읽기 전용) 폴링 중" aria-label="LIVE — 실시간 DB 연결">
        <span aria-hidden className="live-dot size-2 rounded-full bg-status-healthy"/>
        LIVE
      </span>);
    }
    // 데모 화면: 실제 설비·수집기와 연결돼 있지 않다는 것을 배지에서 먼저 알린다.
    const tip = "데모 데이터 — 실제 설비 수집 상태가 아닙니다 (브라우저 내 시뮬레이터)";
    return (<span className="inline-flex h-6 items-center gap-1.5 rounded-full border border-status-warning/40 bg-status-warning/10 px-2 text-[11px] font-semibold text-status-warning" title={tip} aria-label={tip}>
      <span aria-hidden className="size-2 rounded-full bg-status-warning"/>
      데모 데이터
    </span>);
}
function SystemBadge() {
    const status = usePipelineStore(selectSystemStatus);
    const mode = usePipelineStore(selectMode);
    const Icon = SYSTEM_ICON[status];
    const label = mode === "idle" ? "상태 확인 중" : SYSTEM_LABEL[status];
    return (<span className="inline-flex h-6 items-center gap-1.5 rounded-md border border-border bg-bg-elevated px-2 text-xs font-medium" style={{ color: mode === "idle" ? "var(--fg-muted)" : SYSTEM_COLOR[status] }} title="전체 시스템 상태 (수집기 노드 기준: 오류 > 경고 > 정상)" role="status">
      <Icon aria-hidden size={14}/>
      {label}
    </span>);
}
function Stat({ icon: Icon, label, value, title, className, }) {
    return (<div className={cn("flex min-w-0 items-center gap-1.5 text-xs", className)} title={title}>
      <Icon aria-hidden size={13} className="shrink-0 text-fg-subtle"/>
      <span className="hidden text-fg-subtle xl:inline">{label}</span>
      <span className="tabular truncate font-semibold text-fg">{value}</span>
    </div>);
}
function Stats({ compact }) {
    const summary = usePipelineStore(selectSummary);
    const generatedAt = usePipelineStore(selectGeneratedAt);
    const mode = usePipelineStore(selectMode);
    const now = useNow(1000);
    const has = mode !== "idle";
    const rel = now != null && generatedAt ? formatRelative(generatedAt, now) : "—";
    return (<div className={cn("flex min-w-0 items-center gap-3 md:gap-4", compact && "gap-2")}>
      <Stat icon={Waves} label="처리량" value={has ? `${formatNumber(summary.totalThroughput, summary.totalThroughput >= 100 ? 0 : 1)} shots/min` : "—"} title="전체 설비 처리량 합계 (shots/min, 최근 10분 평균)"/>
      <Stat icon={Gauge} label="평균 지연" value={has ? formatLatency(summary.avgLatency) : "—"} title="수집기 heartbeat 나이 평균 (초)" className={cn(compact && "hidden sm:flex")}/>
      <Stat icon={Percent} label="오류율" value={has ? formatPercent(summary.errorRate) : "—"} title="오류 상태 노드 비율 (%)" className={cn(compact && "hidden sm:flex")}/>
      <Stat icon={Clock} label="업데이트" value={rel} title={generatedAt ? `마지막 스냅샷: ${formatTimeTooltip(generatedAt)}` : "아직 스냅샷 없음"} className={cn(compact && "hidden md:flex")}/>
    </div>);
}
export const DashboardHeader = memo(function DashboardHeader({ breakpoint, sidebarOpen, detailOpen, onToggleSidebar, onToggleDetail, }) {
    const paused = usePipelineStore(selectPaused);
    const { togglePaused, requestFitView } = usePipelineStore(selectActions);
    const compact = breakpoint === "mobile" || breakpoint === "tablet";
    const showPanelToggles = breakpoint !== "wide";
    return (<header className="flex h-[var(--header-height)] min-w-0 items-center gap-3 border-b border-border bg-bg-elevated px-3 md:px-4" role="banner">
      {showPanelToggles && (<IconButton label={sidebarOpen ? "노드 목록 닫기" : "노드 목록 열기"} onClick={onToggleSidebar} active={sidebarOpen}>
          <PanelLeft size={16}/>
        </IconButton>)}

      <div className="flex min-w-0 shrink items-center gap-2 sm:shrink-0">
        <span aria-hidden className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
          <Workflow size={18}/>
        </span>
        <h1 className="truncate text-sm font-bold tracking-tight md:text-base">데이터 수집 흐름</h1>
      </div>

      <ModeBadge />
      <div className={cn(compact && "hidden sm:block")}>
        <SystemBadge />
      </div>

      <div className="mx-1 hidden h-6 w-px bg-border sm:block" aria-hidden/>

      <div className={cn("min-w-0 flex-1 overflow-hidden", breakpoint === "mobile" && "hidden")}>
        <Stats compact={compact}/>
      </div>
      {breakpoint === "mobile" && <div className="flex-1" aria-hidden/>}

      <div className="flex shrink-0 items-center gap-1.5">
        <IconButton label={paused ? "재생 (시뮬레이터 재개) — Space" : "일시정지 (시뮬레이터 정지) — Space"} onClick={togglePaused} active={paused} className={cn(paused && "border-status-warning/60 text-status-warning")}>
          {paused ? <Play size={16}/> : <Pause size={16}/>}
        </IconButton>
        <IconButton label="전체 화면 맞춤 (그래프 전체 보기)" onClick={requestFitView}>
          <Maximize2 size={16}/>
        </IconButton>
        {showPanelToggles && (<IconButton label={detailOpen ? "상세 패널 닫기" : "상세 패널 열기"} onClick={onToggleDetail} active={detailOpen}>
            <PanelRight size={16}/>
          </IconButton>)}
      </div>
    </header>);
});
